import { TabNode } from "flexlayout-react";
import { useMemo, useState, useEffect } from "react";
import { Button } from "@/components/ui/button.tsx";
import { useDashboard } from "@/lib/store.ts";
import { useGrid } from "@/hooks/useGrid.ts";
import { IFavoriteFolder } from "@/domain/interfaces/dashboard.interface.ts";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu.tsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { EWidgetType } from "@/domain/enums/dashboard.enum.ts";
import {
  FaRegStar,
  FaSquare,
  FaSquareCheck,
  FaStar,
  FaSquareMinus,
} from "react-icons/fa6";

export function FilterByFavorites({ node }: { node: TabNode }) {
  const { favorites } = useDashboard((state) => ({
    favorites: state.favorites,
  }));
  const { updateTabConfig, getNodeConfig } = useGrid(node.getId());
  const config = getNodeConfig();
  const filterByFavorites = useMemo(
    () => config?.filterByFavorites || [],
    [config?.filterByFavorites],
  );
  const checkedFolderIds = useMemo(
    () => config?.checkedFolderIds || [],
    [config?.checkedFolderIds],
  );

  const isFolderInFilter = useMemo(() => {
    return (folder: IFavoriteFolder) => checkedFolderIds.includes(folder.id);
  }, [checkedFolderIds]);

  const isSymbolInFilter = useMemo(() => {
    return (symbol: string) => {
      // If any folders are selected, check if symbol is in those folders
      if (checkedFolderIds.length > 0) {
        return favorites.some(
          (folder) =>
            checkedFolderIds.includes(folder.id) &&
            folder.symbols.includes(symbol),
        );
      }

      // Otherwise check individual symbol selection
      return filterByFavorites.includes(symbol);
    };
  }, [filterByFavorites, checkedFolderIds, favorites]);

  const getFolderCheckState = useMemo(() => {
    return (folder: IFavoriteFolder) => {
      if (checkedFolderIds.includes(folder.id)) {
        return "checked";
      }

      const selectedSymbolsInFolder = folder.symbols.filter((symbol) =>
        filterByFavorites.includes(symbol),
      );
      if (selectedSymbolsInFolder.length > 0) {
        return "intermediate";
      }

      return "unchecked";
    };
  }, [checkedFolderIds, filterByFavorites]);

  useEffect(() => {
    // Get all symbols from checked folders
    const symbolsFromCheckedFolders = new Set<string>();
    favorites.forEach((folder) => {
      if (checkedFolderIds.includes(folder.id)) {
        folder.symbols.forEach((symbol) =>
          symbolsFromCheckedFolders.add(symbol),
        );
      }
    });

    // Add individually selected symbols
    const allSelectedSymbols = new Set([
      ...Array.from(symbolsFromCheckedFolders),
      ...filterByFavorites.filter(
        (symbol) =>
          // Only keep individually selected symbols that aren't in checked folders
          !Array.from(symbolsFromCheckedFolders).includes(symbol),
      ),
    ]);

    const updatedFilter = Array.from(allSelectedSymbols);

    if (
      JSON.stringify(updatedFilter.sort()) !==
      JSON.stringify([...filterByFavorites].sort())
    ) {
      updateTabConfig({
        filterByFavorites: updatedFilter,
        checkedFolderIds,
      });
    }
  }, [favorites, checkedFolderIds, filterByFavorites, updateTabConfig]);

  const widgetType = node.getComponent() as EWidgetType;
  if (
    widgetType === EWidgetType.controlPanel ||
    widgetType === EWidgetType.chart
  ) {
    return null;
  }

  const resetFilter = () => {
    updateTabConfig({
      filterByFavorites: [],
      checkedFolderIds: [],
    });
    // setIsOpen(false);
  };

  const toggleFolder = (folder: IFavoriteFolder) => {
    const newCheckedFolderIds = checkedFolderIds.includes(folder.id)
      ? checkedFolderIds.filter((id) => id !== folder.id)
      : [...checkedFolderIds, folder.id];

    updateTabConfig({
      checkedFolderIds: newCheckedFolderIds,
      filterByFavorites: [], // Reset individual symbols when using folders
    });
  };

  const toggleFilterByFavoritesSymbol = (symbol: string) => {
    const newFilterByFavorites = filterByFavorites.includes(symbol)
      ? filterByFavorites.filter((s) => s !== symbol)
      : [...filterByFavorites, symbol];

    updateTabConfig({
      filterByFavorites: newFilterByFavorites,
      checkedFolderIds: [], // Reset folder selection when using individual symbols
    });
    // setIsOpen(false);
  };

  return (
    <>
      <DropdownMenu modal>
        <DropdownMenuTrigger asChild>
          <Button
            className="text-white hover:bg-[#333333]"
            size="xxs"
            variant="ghost"
          >
            {filterByFavorites?.length ? (
              <i className="fa-solid fa-star text-xs"></i>
            ) : (
              <i className="fa-regular fa-star text-xs"></i>
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-56 bg-black">
          <DropdownMenuLabel>Filter By Favorites</DropdownMenuLabel>
          <DropdownMenuSeparator />

          <DropdownMenuItem
            onMouseDown={(e) => {
              e.stopPropagation();
              resetFilter();
            }}
          >
            <FontAwesomeIcon icon={faXmark} />
            <span>Reset filter</span>
          </DropdownMenuItem>

          <DropdownMenuSeparator />

          {favorites.map((folder) =>
            folder.symbols.length ? (
              <DropdownMenuSub key={folder.id}>
                <DropdownMenuSubTrigger
                  style={{
                    backgroundColor: folder.color,
                  }}
                  className="cursor-pointer"
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    toggleFolder(folder);
                  }}
                >
                  {getFolderCheckState(folder) === "checked" ? (
                    <FaSquareCheck />
                  ) : getFolderCheckState(folder) === "intermediate" ? (
                    <FaSquareMinus />
                  ) : (
                    <FaSquare />
                  )}
                  <span>{folder.name}</span>
                </DropdownMenuSubTrigger>
                <DropdownMenuPortal>
                  <DropdownMenuSubContent
                    onSelect={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                  >
                    {folder.symbols.map((symbol) => (
                      <DropdownMenuItem
                        key={symbol}
                        onSelect={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          toggleFilterByFavoritesSymbol(symbol);
                        }}
                      >
                        {isSymbolInFilter(symbol) ? (
                          <i className="fa-solid fa-square-check" />
                        ) : (
                          <i className="fa-regular fa-square" />
                        )}
                        {symbol}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuSubContent>
                </DropdownMenuPortal>
              </DropdownMenuSub>
            ) : (
              <DropdownMenuItem
                key={folder.id}
                className="cursor-pointer"
                style={{
                  backgroundColor: folder.color,
                }}
                onMouseDown={(e) => {
                  e.stopPropagation();
                  toggleFolder(folder);
                }}
              >
                {getFolderCheckState(folder) === "checked" ? (
                  <FaSquareCheck />
                ) : getFolderCheckState(folder) === "intermediate" ? (
                  <FaSquareMinus />
                ) : (
                  <FaSquare />
                )}
                {folder.name}
              </DropdownMenuItem>
            ),
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
}
